import { Badge } from "../../components/ui/badge";
import { Box } from "../../components/ui/box";
import { Button } from "../../components/ui/button";
import { POPOVER_POPUP_WIDTH } from "../../components/ui/popover";
import { Text } from "../../components/ui/text";
import type { ReactNode } from "react";
import type { Omittable } from "../../lib/props";

/**
 * The selector's list grammar, in one place so the sections cannot drift.
 *
 * Two edges, each held by everything of its kind. Every FRAME — a row's
 * wash, the search well, the pool tabs, the footer — stands 6px inside the
 * popup: the row's `mx-1.5` from the sides, and the list area's 6px gutter
 * above and below. Every line of TEXT starts at 10px: the row pads its
 * content 4px inside its wash, and every label, heading and chip row in the
 * popup takes the same 10px, whichever section it belongs to. A section that
 * spells its own edge instead lands a step off the section above it, and two
 * sections in one popup is all it takes to read as two designs.
 */

/**
 * The row's visual column. Fixed and centered, so a pool of photo avatars and
 * a pool of icon tiles start their labels at the same x — the rail is what
 * makes the column a column, rather than whatever width the widest glyph in
 * that particular pool happens to be. It is also what sets the row's height:
 * 24px of rail plus the row's 4px above and below is the 32px row.
 *
 * A consumer supplying `avatar.render` should fill the rail rather than pick
 * its own size, so it is exported: a tile that renders smaller leaves the row
 * looking under-filled next to a photo avatar that does not.
 */
export const SELECTOR_VISUAL_RAIL_PX = 24;

/**
 * THE selector width — the `md` preset and the default. A searchable list of
 * entities is one kind of thing, so it is one width, and a call site states a
 * width only when its content genuinely differs (a `card`/`builder`/`panel`
 * floor, or `fit` to floor at the trigger).
 *
 * Exported for a HOST that pins its own popup around an inline selector. Most
 * hosts should pin nothing: `PopoverPopup` sizes to its content, so the inline
 * branch's width request already lands the popup here.
 */
export const SELECTOR_CONTENT_WIDTH = POPOVER_POPUP_WIDTH.picker;

export type EntitySelectorSectionLabelAction = Omittable<{
  /** The action's verb — "Edit", "Manage". */
  label: string;
  onClick: () => void;
}>;

export type EntitySelectorSectionLabelProps = Omittable<{
  label?: string;
  /** Count badge after the label. Omit for a bare label. */
  count?: number;
  /**
   * A trailing text action at the label's far end — the section's door to
   * wherever its rows are managed. Sits on the label's own rung, so the row
   * stays 32px with or without it.
   */
  action?: EntitySelectorSectionLabelAction;
  /**
   * A trailing control at the label's far end when the section's door is
   * not one verb — a menu over the rows, a toggle. Rendered as given, on the
   * same rung; pass one of `action` or `trailing`.
   */
  trailing?: ReactNode;
}>;

/**
 * The section-titling rung: faint, not muted (the Command group-label rung).
 * A section name is wayfinding — muted competes with the rows' own secondary
 * text, which is real content.
 */
function EntitySelectorSectionLabel({
  label,
  count,
  action,
  trailing,
}: EntitySelectorSectionLabelProps) {
  if (!label) return null;
  return (
    <Box
      direction="row"
      align="center"
      justify="between"
      gap="xs"
      // Inline in the chip row it is a flex sibling of a scroller, which would
      // otherwise squeeze the label out of the way.
      shrink={false}
      // The list's 10px reading edge, where the row content below starts.
      className="px-2.5"
      // The list's 32px rung, matching the rows and the search field. A fixed
      // height rather than padding around the text line, so the 24px trailing
      // action stands inside the rung instead of growing it.
      height={32}
    >
      <Box direction="row" align="center" gap="xs" shrink>
        <Text size="xs" weight="medium" variant="faint" truncate>
          {label}
        </Text>
        {count != null && count > 0 && (
          <Badge color="secondary" size="sm">
            {count}
          </Badge>
        )}
      </Box>
      {action ? (
        // Pulled back by its own inset so the verb ends where the label
        // begins, mirrored: the text sits on the rows' 10px edge, the hover
        // wash reaches toward the pool's edge.
        <Box marginRightNegative="sm" shrink={false}>
          <Button variant="ghost" size="xs" onClick={action.onClick}>
            <Text size={null} variant="muted">
              {action.label}
            </Text>
          </Button>
        </Box>
      ) : trailing ? (
        // A CONTROL pulls back 4px, to the 6px edge: its box ends where the
        // search well above and the row washes below end, so the popover's
        // right edge reads as one line. The text verb pulls back further —
        // its text, not its wash, is what lines up.
        <Box marginRightNegative="xs" shrink={false}>
          {trailing}
        </Box>
      ) : null}
    </Box>
  );
}

EntitySelectorSectionLabel.displayName = "EntitySelectorSectionLabel";

export { EntitySelectorSectionLabel };
